import { useUserQuery } from '@/shared/hooks/useUserQuery';
import { isInternalRoute } from '@/shared/internalPaths';
import { ApiUser } from '@/types/user';
import { urlHelper } from '@/utils';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';

export const usePathsGuard = () => {
  const { data: user } = useUserQuery();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    //User null means not authenticated
    if (user === null && (isInternalRoute(pathname) || isPostRegistrationFlow(pathname))) {
      const currentUrl = `${pathname}${window.location.search}`;
      const loginUrl = `${urlHelper.login()}?callbackUrl=${encodeURIComponent(currentUrl)}`;
      return router.replace(loginUrl);
    }

    if (!user) {
      return; // User undefined means app was just loaded, we don't know if user is authenticated or not
    }

    const searchParams = new URLSearchParams(window.location.search);
    const callbackUrl = searchParams.get('callbackUrl');

    if (!isEmailConfirmationFlow(pathname) && !user.verified) {
      if (callbackUrl && callbackUrl.includes(urlHelper.verifyEmail())) {
        // Check if callback url is to verify email - meaning user was logged out, and tried to confirm email - we should make the flow natural to user, so when he logs in then continues the flow
        const sanitizedCallbackUrl = callbackUrl.startsWith('/') ? callbackUrl : urlHelper.verifyAccount();
        return router.replace(sanitizedCallbackUrl);
      }
      return router.replace(urlHelper.verifyAccount());
    }

    if (!isPostRegistrationFlow(pathname) && shouldRedirectToCompleteRegistration(user)) {
      return router.replace(urlHelper.completeRegistration());
    }

    if (isEmailConfirmationFlow(pathname) && user.verified) {
      return router.replace(
        shouldRedirectToCompleteRegistration(user) ? urlHelper.completeRegistration() : urlHelper.assets()
      );
    }

    if (isPathOnlyForUnauthenticated(pathname)) {
      if (callbackUrl) {
        const sanitizedCallbackUrl = callbackUrl.startsWith('/') ? callbackUrl : urlHelper.assets();
        return router.replace(sanitizedCallbackUrl);
      }
      return router.replace(urlHelper.assets());
    }
  }, [user, pathname, router]);
};

//This methods are duplicated in middleware.ts. I want to keep it that way, as I want to get rid of middleware eventually, please keep in sync.
function isEmailConfirmationFlow(pathname: string) {
  return pathname.startsWith(urlHelper.verifyEmail()) || pathname.startsWith(urlHelper.verifyAccount());
}

function isPostRegistrationFlow(pathname: string) {
  return isEmailConfirmationFlow(pathname) || pathname.startsWith(urlHelper.completeRegistration());
}

export function shouldRedirectToCompleteRegistration(user: ApiUser) {
  if (user.first_name) {
    return false;
  }

  if (new Date(user.created_at ?? 0) < new Date('2024-09-18')) {
    return false;
  }

  return true;
}

function isPathOnlyForUnauthenticated(pathname: string) {
  return (
    pathname.startsWith(urlHelper.login()) ||
    pathname.startsWith(urlHelper.register()) ||
    pathname.startsWith(urlHelper.forgotPassword()) ||
    pathname.startsWith(urlHelper.sso())
  );
}
